import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";

const CloseIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const EditQuestionModal = ({ isOpen, onClose, onSave, loading, isSaved, initialQuestion }) => {
  const [questionText, setQuestionText] = useState("");

  // ✅ Populate input text whenever modal opens with a question
  useEffect(() => {
    if (isOpen && initialQuestion) {
      setQuestionText(initialQuestion.name || "");
    } else {
      setQuestionText("");
    }
  }, [isOpen, initialQuestion]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!questionText.trim()) {
      toast.error("Please enter a question");
      return;
    }
    onSave(questionText); 
  };

  // ✅ Check if text was actually changed to enable the Save button
  const isChanged = initialQuestion && questionText.trim() !== initialQuestion.name;

  // ✅ Disabled until user changes text, and during loading/added states
  const isButtonDisabled = !isChanged || !questionText.trim() || loading || isSaved;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-[12px] shadow-xl w-full max-w-[600px] mx-auto overflow-hidden animate-in fade-in zoom-in duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-3 pb-2 border-b border-black/[0.08]">
          <div>
            <h2 className="text-[18px] font-semibold text-[#18101C] font-outfit">
              Edit Evaluation Question
            </h2>
            <p className="text-[14px] text-[#71717A] font-outfit">
              Update the custom question that will be answered by AI.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-[#F4F4F5] text-[#71717A] transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <label className="block text-[15px] font-medium text-[#18101C] mb-2 font-outfit">
            Edit your question*
          </label>
          <input
            type="text"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="e.g. Does the RFP specify a submission deadline? "
            className="w-full h-11 min-h-[44px] px-3 py-2.5 text-[15px] placeholder-[#00000040] font-normal leading-[20px] hover:text-[14px] focus:text-[14px] bg-white border border-black/15 rounded-lg text-[#18101C] focus:outline-none focus:border-[#8B55DD] focus:ring-[3px] focus:ring-[rgba(139,85,221,0.15)]"
            style={{ fontSize: "14px", lineHeight: "20px" }}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-6 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[8px] border border-black/20 text-[#3F3F46] text-[14px] font-medium font-outfit hover:bg-[#F9FAFB] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isButtonDisabled} 
            className="px-4 py-2 rounded-[8px] bg-[#A04A97] text-white text-[14px] font-medium font-outfit hover:bg-[#8E4186] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* ✅ Label cycles: Save -> Saving... -> Saved */}
            {isSaved ? "Saved" : loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditQuestionModal;
